import { CSSEvent, Store } from '../../types/model';
import { config } from '../../config';
import { DIGITAL, PHYSICAL } from '../../enums';

type SchemaType = {
  '@context': string;
  '@type': string;
  name: string;
  eventAttendanceMode: string;
  eventStatus: string;
  startDate: string;
  endDate: string;
  location: {
    '@type': string;
    name?: string;
    url?: string;
    address?: {
      '@type': string;
      streetAddress: string;
      addressLocality: string;
      postalCode: string;
      addressCountry: string;
    };
  };
  image?: string[];
  description: string;
  organizer: {
    '@type': 'Organization';
    name: 'IKEA';
    url: string;
  };
};

export default function getEventDetailsSeo(
  stores: Store[],
  event: CSSEvent,
  t: Function
) {
  const {
    location,
    visibilityState = 'ACTIVE',
    eventDetails,
    eventImagePath,
    timeSlots,
  } = event;
  const eventStoreNo = location.store.storeNo;
  const storesStreetAddr = stores[eventStoreNo]?.street;
  const storesCityAddr = stores[eventStoreNo]?.city;
  const storesPostalCode = stores[eventStoreNo]?.zipCode;
  const storesUrlName = stores[eventStoreNo]?.slug;

  const eventAttendanceMode: { [key: string]: string } = {
    DIGITAL: 'https://schema.org/OnlineEventAttendanceMode',
    PHYSICAL: 'https://schema.org/OfflineEventAttendanceMode',
  };

  const eventStatus = {
    REMOVED: 'https://schema.org/EventCancelled',
    ACTIVE: 'https://schema.org/EventScheduled',
    PAST: 'https://schema.org/EventCancelled',
  };

  const schema: SchemaType = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: eventDetails.eventName,
    eventAttendanceMode: eventAttendanceMode[location.type],
    eventStatus: eventStatus[visibilityState],
    location: {
      '@type': 'Place',
    },
    description: eventDetails.eventIntroduction,
    image: [eventImagePath],
    startDate: timeSlots[0]?.startDate,
    endDate: timeSlots[0]?.endDate,
    organizer: {
      '@type': 'Organization',
      name: 'IKEA',
      url: `https://www.ikea.com/${config.country}/${config.language}/`,
    },
  };
  if (location.type === DIGITAL) {
    schema['location'] = {
      '@type': 'VirtualLocation',
      url: location.url || '',
    };
  } else if (location.type === PHYSICAL) {
    schema['location'] = {
      '@type': 'Place',
      name: location.store.storeName,
      address: {
        '@type': 'PostalAddress',
        streetAddress: storesStreetAddr,
        addressLocality: storesCityAddr,
        postalCode: storesPostalCode,
        addressCountry: location.store.countryCode,
      },
    };
  }

  const metaDescriptionKey =
    location.type === DIGITAL
      ? 'storeEventCustomer.digitalEventDescription'
      : 'storeEventCustomer.physicalEventDescription';
  const metaDescription = t(metaDescriptionKey, {
    shortDescription: eventDetails.eventIntroduction,
    storeName: location.store.storeName,
  });

  const metacontent = (eventStatus.PAST === eventStatus[visibilityState]) ? 'noindex, nofollow' : 'index, follow';

  const meta = [
    { name: 'description', content: metaDescription },
    { name: 'robots', content: metacontent },
    { property: 'og:title', content: `${eventDetails.eventName} - IKEA` },
    { property: 'og:description', content: eventDetails.eventIntroduction },
    { property: 'og:type', content: 'website' },
    { property: 'og:image', content: eventImagePath },
    {
      property: 'og:url',
      content: `https://www.ikea.com/${config.country}/${config.language}/stores/events/${storesUrlName}/${event?.urlId}`,
    },
  ];

  const title = t('event.eventTitle', {
    storeName: event.location.store.storeName,
    eventName: event?.eventDetails.eventName,
  });

  return { jsonLd: schema, title, meta, canonicalPath: storesUrlName };
}
